import React from 'react';
import { Box, useTheme } from '@mui/material';
import { Card } from '@/components/molecules/Card';
import { Typography } from '@/components/atoms';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { StatCardProps, StatCardVariant } from './StatCard.props';

export const StatCard: React.FC<StatCardProps> = ({
  value,
  label,
  icon,
  variant = 'primary',
  trend = 'neutral',
  selected = false,
  onClick,
  clickable = false,
  sx = {},
}) => {
  const theme = useTheme();

  // Get colors based on variant
  const getColors = (variant: StatCardVariant) => {
    if (variant === 'warning') {
      return {
        borderColor: theme.palette.M3.sys.dark.error,
        iconBgColor: theme.palette.M3.sys.dark['on-error-container'],
        iconColor: theme.palette.M3.sys.light['on-error-container'],
        stateBgColor:
          theme.palette.M3['state-layers'].light.error['opacity-0.08'],
      };
    }
    return {
      borderColor: theme.palette.M3.sys.dark.primary,
      iconBgColor: theme.palette.M3.sys.light['primary-container'],
      iconColor: theme.palette.M3.sys.light['on-primary-container'],
      stateBgColor:
        theme.palette.M3['state-layers'].light.primary['opacity-0.08'],
    };
  };

  const colors = getColors(variant);

  // Render trend arrow
  const renderTrendIcon = () => {
    if (trend === 'neutral') return null;

    const ArrowIcon = trend === 'up' ? ArrowUpwardIcon : ArrowDownwardIcon;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ArrowIcon
          sx={{
            fontSize: 22,
            color: theme.palette.M3.sys.light['on-secondary-container'],
          }}
        />
      </Box>
    );
  };

  return (
    <Card
      onClick={clickable ? onClick : undefined}
      sx={{
        borderLeft: `${theme.unit(1.5)} solid ${colors.borderColor}`,
        borderRadius: theme.unit(2),
        padding: theme.unit(4),
        cursor: clickable ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        ...(selected && {
          boxShadow: theme.shadows[4],
          backgroundColor: colors.stateBgColor,
        }),
        '&:hover': clickable
          ? {
              boxShadow: theme.shadows[2],
              backgroundColor: colors.stateBgColor,
            }
          : {},
        ...sx,
      }}
      content={
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.unit(1),
            margin: theme.unit(-5), // Counteract CardContent default padding
            marginBottom: theme.unit(0),
            paddingLeft: theme.unit(3),
          }}
        >
          {/* First Row: Value with Trend (left) and Icon (right) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            {/* Left: Value and Trend */}
            <Box
              sx={{
                display: 'flex',
                gap: theme.unit(1),
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  ...theme.typography.M3.display.small,
                  color: theme.palette.M3.sys.light['on-primary-fixed'],
                }}
              >
                {value}
              </Typography>
              {renderTrendIcon()}
            </Box>

            {/* Right: Icon container */}
            <Box
              sx={{
                position: 'relative',
                width: theme.unit(12),
                height: theme.unit(12),
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: colors.iconBgColor,
                  borderRadius: theme.unit(1.5),
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: theme.unit(7.5),
                  height: theme.unit(7.5),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colors.iconColor,
                  '& .MuiSvgIcon-root': {
                    color: colors.iconColor,
                  },
                }}
              >
                {icon}
              </Box>
            </Box>
          </Box>

          {/* Second Row: Label text */}
          <Typography
            sx={{
              ...theme.typography.M3.body.medium,
              color: theme.palette.M3.sys.light['on-surface'],
              width: '100%',
              whiteSpace: 'pre-wrap',
            }}
          >
            {label}
          </Typography>
        </Box>
      }
    />
  );
};

export default StatCard;
